import React, { useEffect, useState } from "react";
import portalApi from "../../api/portalClient";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { useSort, sortRows, SortableTh } from "../../components/SortableTh";
import PasswordChoice, { passwordChoiceProblem } from "../../components/PasswordChoice";

/** Portal administrator's "Manage organization" area. Customer-scoped, role-gated
 * self-service over the same M365 (Graph) + on-prem AD (agent relay) engines
 * staff use. Highest-value actions first: enable/disable + reset password. */

type Tab = "m365" | "ad";

interface M365User {
  id: number;
  upn: string;
  display_name?: string | null;
  account_enabled: boolean;
  on_prem_synced: boolean;
}
interface ADUser {
  id: number;
  sam_account_name: string;
  display_name?: string | null;
  enabled: boolean;
  locked: boolean;
}

export default function PortalManage() {
  const { user } = usePortalAuth();
  const [tab, setTab] = useState<Tab>("m365");

  return (
    // Was max-w-4xl (896px), which squeezed the actions column so hard that
    // "Manage in AD" was clipped off the right edge and unreachable. A user
    // table needs the width.
    <div className="max-w-6xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Manage {user?.customer_name}</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        Add people, unlock and enable accounts, reset passwords, and offboard leavers — across your
        Microsoft 365 tenant and on-prem Active Directory.
      </p>

      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-5">
        {[
          { key: "m365" as Tab, label: "Microsoft 365" },
          { key: "ad" as Tab, label: "Active Directory" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={
              "px-4 py-2 text-sm border-b-2 -mb-px transition-colors " +
              (tab === t.key
                ? "border-brand text-brand dark:text-brand-light font-semibold"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "m365" ? <M365Panel /> : <ADPanel />}
    </div>
  );
}

function PasswordReveal({
  who,
  password,
  onClose,
}: {
  who: string;
  password: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">New password for {who}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
          Shown once — copy it and share it securely. They'll be asked to change it on next sign-in.
        </p>
        <div className="bg-gray-900 text-green-300 font-mono text-sm rounded-lg px-3 py-3 select-all break-all mb-3">
          {password}
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => navigator.clipboard?.writeText(password)}
            className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg"
          >
            Copy
          </button>
          <button onClick={onClose} className="bg-brand text-white text-sm px-4 py-2 rounded-lg">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function useNotice() {
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  return { notice, setNotice };
}

interface Sku {
  sku_id: string;
  sku_part_number: string;
  available: number;
}

function M365Panel() {
  const [users, setUsers] = useState<M365User[] | null>(null);
  const [skus, setSkus] = useState<Sku[]>([]);
  const [busy, setBusy] = useState<number | null>(null);
  const [reveal, setReveal] = useState<{ who: string; password: string } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ display_name: "", user_principal_name: "", sku_id: "" });
  // undefined = let the server generate one (the default).
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const [licenseTarget, setLicenseTarget] = useState<M365User | null>(null);
  const { notice, setNotice } = useNotice();

  const load = () =>
    portalApi
      .get("/api/v1/portal/manage/m365/users")
      .then((r) => setUsers(r.data))
      .catch(() => setUsers([]));
  useEffect(() => {
    load();
    portalApi.get("/api/v1/portal/manage/m365/skus").then((r) => setSkus(r.data || [])).catch(() => setSkus([]));
  }, []);

  const act = async (
    u: M365User,
    action: "disable" | "enable" | "reset-password" | "offboard" | "reset-mfa",
  ) => {
    if (
      action === "reset-mfa" &&
      !window.confirm(
        `Clear the sign-in methods for ${u.upn}?

They will be asked to set up their authenticator app again next time they sign in. Their password is not changed.`,
      )
    )
      return;
    if (action === "disable" && !window.confirm(`Disable ${u.upn}? They'll lose access.`)) return;
    if (action === "reset-password" && !window.confirm(`Reset the password for ${u.upn}?`)) return;
    // Spell out the licence release: it is the part with a monthly bill
    // attached, and the part people are surprised by if it isn't mentioned.
    if (
      action === "offboard" &&
      !window.confirm(
        `Offboard ${u.upn}?\n\nThis blocks sign-in, signs them out everywhere, clears their MFA methods, and releases any licences they hold so you stop paying for the seat.`,
      )
    )
      return;
    setBusy(u.id);
    setNotice(null);
    try {
      const r = await portalApi.post(`/api/v1/portal/manage/m365/users/${u.id}/${action}`);
      if (r.data.generated_password) setReveal({ who: u.upn, password: r.data.generated_password });
      // ok=false means it partly worked — say so rather than reporting a clean
      // run over a licence that would not detach.
      else setNotice({ kind: r.data.ok === false ? "err" : "ok", text: r.data.message || "Done" });
      load();
    } catch (e: any) {
      setNotice({ kind: "err", text: e?.response?.data?.detail || "Action failed" });
    } finally {
      setBusy(null);
    }
  };

  const onboard = async (e: React.FormEvent) => {
    e.preventDefault();
    // The server re-checks; this just saves a round trip and a vague error.
    const bad = passwordChoiceProblem(password, {
      username: form.user_principal_name.trim(),
      displayName: form.display_name.trim(),
    });
    if (bad) {
      setNotice({ kind: "err", text: bad });
      return;
    }
    setAdding(true);
    setNotice(null);
    try {
      const r = await portalApi.post("/api/v1/portal/manage/m365/users", {
        display_name: form.display_name.trim(),
        user_principal_name: form.user_principal_name.trim(),
        sku_id: form.sku_id || null,
        password: password ?? null,
      });
      setShowAdd(false);
      setForm({ display_name: "", user_principal_name: "", sku_id: "" });
      setPassword(undefined);
      if (r.data.generated_password)
        setReveal({ who: form.user_principal_name.trim(), password: r.data.generated_password });
      else setNotice({ kind: "ok", text: r.data.message || "User created" });
      load();
    } catch (e: any) {
      setNotice({ kind: "err", text: e?.response?.data?.detail || "Couldn't create the user" });
    } finally {
      setAdding(false);
    }
  };

  if (users === null) return <p className="text-sm text-gray-400">Loading…</p>;

  return (
    <>
      <div className="flex justify-end mb-3">
        <button
          onClick={() => setShowAdd(true)}
          className="text-xs px-3 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark"
        >
          + Add Microsoft 365 user
        </button>
      </div>
      {notice && <Notice notice={notice} />}
      {users.length === 0 ? (
        <EmptyState text="No Microsoft 365 users found yet. Add one above, or if your Microsoft tenant isn't connected, ask your provider to finish setup." />
      ) : (
        <UserTable
          rows={users.map((u) => ({
            id: u.id,
            primary: u.upn,
            secondary: u.display_name || "",
            enabled: u.account_enabled,
            note: u.on_prem_synced ? "Synced from AD" : "",
            disableActions: u.on_prem_synced,
          }))}
          busy={busy}
          onEnable={(id) => act(users.find((x) => x.id === id)!, "enable")}
          onDisable={(id) => act(users.find((x) => x.id === id)!, "disable")}
          onReset={(id) => act(users.find((x) => x.id === id)!, "reset-password")}
          onOffboard={(id) => act(users.find((x) => x.id === id)!, "offboard")}
          onLicenses={(id) => setLicenseTarget(users.find((x) => x.id === id) || null)}
          onResetMfa={(id) => act(users.find((x) => x.id === id)!, "reset-mfa")}
        />
      )}

      {licenseTarget && (
        <LicenseModal
          user={licenseTarget}
          skus={skus}
          onClose={() => setLicenseTarget(null)}
          onChanged={load}
        />
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Add Microsoft 365 user</h2>
            <form onSubmit={onboard} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Full name</label>
                <input
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  required
                  className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email (UPN)</label>
                <input
                  type="email"
                  value={form.user_principal_name}
                  onChange={(e) => setForm({ ...form, user_principal_name: e.target.value })}
                  required
                  placeholder="jane.doe@yourdomain.com"
                  className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  License <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <select
                  value={form.sku_id}
                  onChange={(e) => setForm({ ...form, sku_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="">No license (assign later)</option>
                  {skus.map((s) => (
                    <option key={s.sku_id} value={s.sku_id}>
                      {s.sku_part_number} ({s.available} available)
                    </option>
                  ))}
                </select>
              </div>
              <PasswordChoice
                value={password}
                onChange={setPassword}
                username={form.user_principal_name}
                displayName={form.display_name}
                disabled={adding}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                A mailbox provisions once a license is assigned.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={adding}
                  className="bg-brand text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {adding ? "Creating…" : "Create user"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {reveal && <PasswordReveal {...reveal} onClose={() => setReveal(null)} />}
    </>
  );
}

function ADPanel() {
  const [users, setUsers] = useState<ADUser[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [reveal, setReveal] = useState<{ who: string; password: string } | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", sam_account_name: "", user_principal_name: "", email: "" });
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [adding, setAdding] = useState(false);
  const { notice, setNotice } = useNotice();

  const onboard = async (e: React.FormEvent) => {
    e.preventDefault();
    const bad = passwordChoiceProblem(password, {
      username: form.sam_account_name.trim(),
      displayName: form.name.trim(),
    });
    if (bad) {
      setNotice({ kind: "err", text: bad });
      return;
    }
    setAdding(true);
    setNotice(null);
    try {
      const r = await portalApi.post("/api/v1/portal/manage/ad/users", {
        name: form.name.trim(),
        sam_account_name: form.sam_account_name.trim(),
        user_principal_name: form.user_principal_name.trim() || null,
        email: form.email.trim() || null,
        password: password ?? null,
      });
      setShowAdd(false);
      setForm({ name: "", sam_account_name: "", user_principal_name: "", email: "" });
      setPassword(undefined);
      if (r.data.generated_password)
        setReveal({ who: form.sam_account_name.trim(), password: r.data.generated_password });
      setNotice({
        kind: "ok",
        text: (r.data.message || "Queued") + " — the on-site agent will create it shortly.",
      });
    } catch (e: any) {
      setNotice({ kind: "err", text: e?.response?.data?.detail || "Couldn't create the user" });
    } finally {
      setAdding(false);
    }
  };

  const load = () =>
    portalApi
      .get("/api/v1/portal/manage/ad/users")
      .then((r) => setUsers(r.data))
      .catch(() => setUsers([]));
  useEffect(() => {
    load();
  }, []);

  const act = async (
    u: ADUser,
    action: "disable" | "enable" | "reset-password" | "offboard" | "unlock",
  ) => {
    // Unlock deliberately has no confirm: it grants no access the account did
    // not already have, and it is the one thing somebody does while a colleague
    // stands over their shoulder waiting to get back in.
    if (action === "disable" && !window.confirm(`Disable ${u.sam_account_name}?`)) return;
    if (action === "reset-password" && !window.confirm(`Reset the password for ${u.sam_account_name}?`))
      return;
    if (
      action === "offboard" &&
      !window.confirm(
        `Offboard ${u.sam_account_name}? This disables the account, resets the password, signs them out, and removes group memberships.`,
      )
    )
      return;
    setBusy(u.id);
    setNotice(null);
    try {
      const r = await portalApi.post(`/api/v1/portal/manage/ad/users/${u.id}/${action}`);
      if (r.data.generated_password) setReveal({ who: u.sam_account_name, password: r.data.generated_password });
      else
        setNotice({
          kind: "ok",
          text: (r.data.message || "Queued") + " — the on-site agent will apply it shortly.",
        });
    } catch (e: any) {
      setNotice({ kind: "err", text: e?.response?.data?.detail || "Action failed" });
    } finally {
      setBusy(null);
    }
  };

  if (users === null) return <p className="text-sm text-gray-400">Loading…</p>;

  return (
    <>
      <div className="flex justify-end mb-3">
        <button
          onClick={() => setShowAdd(true)}
          className="text-xs px-3 py-2 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark"
        >
          + Add AD user
        </button>
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-2.5 mb-3">
        Active Directory changes are carried out by your on-site agent, so they take effect within a
        minute or two.
      </p>
      {notice && <Notice notice={notice} />}
      {users.length === 0 ? (
        <EmptyState text="No Active Directory users found. If on-prem AD management isn't set up yet, ask your provider to designate an AD runner." />
      ) : (
        <UserTable
          rows={users.map((u) => ({
            id: u.id,
            primary: u.sam_account_name,
            secondary: u.display_name || "",
            enabled: u.enabled,
            note: "",
            locked: u.locked,
            disableActions: false,
          }))}
          busy={busy}
          onEnable={(id) => act(users.find((x) => x.id === id)!, "enable")}
          onDisable={(id) => act(users.find((x) => x.id === id)!, "disable")}
          onReset={(id) => act(users.find((x) => x.id === id)!, "reset-password")}
          onOffboard={(id) => act(users.find((x) => x.id === id)!, "offboard")}
          onUnlock={(id) => act(users.find((x) => x.id === id)!, "unlock")}
        />
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Add Active Directory user</h2>
            <form onSubmit={onboard} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Full name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Username (login)</label>
                <input
                  value={form.sam_account_name}
                  onChange={(e) => setForm({ ...form, sam_account_name: e.target.value })}
                  required
                  placeholder="jdoe"
                  className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Email / UPN <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="email"
                  value={form.user_principal_name}
                  onChange={(e) => setForm({ ...form, user_principal_name: e.target.value, email: e.target.value })}
                  placeholder="jdoe@corp.local"
                  className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>
              <PasswordChoice
                value={password}
                onChange={setPassword}
                username={form.sam_account_name}
                displayName={form.name}
                disabled={adding}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The account is created by your on-site agent, and the user must change the password
                at first sign-in.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={adding}
                  className="bg-brand text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {adding ? "Queuing…" : "Create user"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {reveal && <PasswordReveal {...reveal} onClose={() => setReveal(null)} />}
    </>
  );
}

function LicenseModal({
  user,
  skus,
  onClose,
  onChanged,
}: {
  user: M365User;
  skus: Sku[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [assigned, setAssigned] = useState<{ sku_id: string; sku_part_number?: string | null }[] | null>(
    null,
  );
  const [addSku, setAddSku] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () =>
    portalApi
      .get(`/api/v1/portal/manage/m365/users/${user.id}/licenses`)
      .then((r) => setAssigned(r.data))
      .catch(() => setAssigned([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = async (action: "assign-license" | "remove-license", sku_id: string) => {
    setBusy(true);
    setErr("");
    try {
      await portalApi.post(`/api/v1/portal/manage/m365/users/${user.id}/${action}`, { sku_id });
      setAddSku("");
      await load();
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "License change failed");
    } finally {
      setBusy(false);
    }
  };

  const assignedIds = new Set((assigned || []).map((a) => a.sku_id));
  const addable = skus.filter((s) => !assignedIds.has(s.sku_id));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Licenses — {user.upn}</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none">
            ×
          </button>
        </div>

        {assigned === null ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : (
          <>
            <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Assigned</div>
            {assigned.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic mb-3">No licenses assigned.</p>
            ) : (
              <div className="space-y-1 mb-3">
                {assigned.map((a) => (
                  <div
                    key={a.sku_id}
                    className="flex items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5"
                  >
                    <span className="text-sm text-gray-900 dark:text-gray-100">{a.sku_part_number || a.sku_id}</span>
                    <button
                      onClick={() => change("remove-license", a.sku_id)}
                      disabled={busy}
                      className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Add a license</div>
            <div className="flex gap-2">
              <select
                value={addSku}
                onChange={(e) => setAddSku(e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">— Select a license —</option>
                {addable.map((s) => (
                  <option key={s.sku_id} value={s.sku_id}>
                    {s.sku_part_number} ({s.available} available)
                  </option>
                ))}
              </select>
              <button
                onClick={() => addSku && change("assign-license", addSku)}
                disabled={busy || !addSku}
                className="text-sm px-3 py-2 bg-brand text-white rounded-lg disabled:opacity-50"
              >
                Assign
              </button>
            </div>
            {err && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{err}</p>}
          </>
        )}
      </div>
    </div>
  );
}

function Notice({ notice }: { notice: { kind: "ok" | "err"; text: string } }) {
  return (
    <p
      className={
        "text-sm px-3 py-2 rounded-lg mb-3 border " +
        (notice.kind === "ok"
          ? "bg-emerald-50 dark:bg-emerald-900/40 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300"
          : "bg-red-50 dark:bg-red-900/40 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300")
      }
    >
      {notice.text}
    </p>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 text-center text-gray-400 dark:text-gray-500 text-sm">
      {text}
    </div>
  );
}

interface Row {
  id: number;
  primary: string;
  secondary: string;
  enabled: boolean;
  note: string;
  disableActions: boolean;
  /** AD only. Orthogonal to `enabled` — a locked account is still enabled. */
  locked?: boolean;
}

/** One action in the row's action group.
 *
 * These were bare underlined text links jammed together with `space-x-3`, on a
 * `whitespace-nowrap` cell inside a page capped at 896px — so on a real tenant
 * the last action was clipped past the right edge. Chips give each action its
 * own hit target and let the group wrap instead of overflowing.
 */
function RowAction({
  label,
  onClick,
  busy,
  tone = "normal",
  title,
}: {
  label: string;
  onClick: () => void;
  busy: boolean;
  tone?: "normal" | "danger" | "primary";
  title?: string;
}) {
  const toneClass =
    tone === "danger"
      ? "border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
      : tone === "primary"
        ? "border-brand/40 text-brand dark:text-brand-light hover:bg-brand/5"
        : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700";
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={title}
      className={`text-xs px-2 py-1 rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${toneClass}`}
    >
      {label}
    </button>
  );
}

function UserTable({
  rows,
  busy,
  onEnable,
  onDisable,
  onReset,
  onOffboard,
  onLicenses,
  onUnlock,
  onResetMfa,
}: {
  rows: Row[];
  busy: number | null;
  onEnable: (id: number) => void;
  onDisable: (id: number) => void;
  onReset: (id: number) => void;
  onOffboard: (id: number) => void;
  onLicenses?: (id: number) => void;
  onUnlock?: (id: number) => void;
  onResetMfa?: (id: number) => void;
}) {
  const userSort = useSort("primary", "asc");
  // A real tenant has hundreds of accounts. Scrolling a wall of them to find
  // one person is the whole reason this screen felt unusable.
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const visible = needle
    ? rows.filter(
        (r) =>
          (r.primary || "").toLowerCase().includes(needle) ||
          (r.secondary || "").toLowerCase().includes(needle),
      )
    : rows;

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-sm">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or sign-in address…"
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg pl-3 pr-8 py-2 text-sm"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm"
            >
              ×
            </button>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {needle ? `${visible.length} of ${rows.length}` : `${rows.length}`}
          {rows.length === 1 ? " account" : " accounts"}
        </span>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm table-fixed">
          <colgroup>
            {/* Fixed shares stop one 60-character UPN from starving the
                actions column on every other row. */}
            <col className="w-1/2" />
            <col className="w-40" />
            <col />
          </colgroup>
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <SortableTh label="User" sortKey="primary" sort={userSort.sort} onSort={userSort.toggle} />
              <SortableTh label="Status" sortKey="enabled" sort={userSort.sort} onSort={userSort.toggle} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {visible.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nobody matches “{q}”.
                </td>
              </tr>
            )}
            {sortRows(visible, userSort.sort, (r, key) => {
              switch (key) {
                case "enabled":
                  return r.enabled ? 1 : 0;
                default:
                  return (r.primary || "").toLowerCase();
              }
            }).map((r) => (
              <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 align-top">
                <td className="px-4 py-3">
                  {/* title= so the full address is still readable when truncated */}
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={r.primary}>
                    {r.primary}
                  </div>
                  {r.secondary && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate" title={r.secondary}>
                      {r.secondary}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${r.enabled ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
                  >
                    {r.enabled ? "Enabled" : "Disabled"}
                  </span>
                  {/* Locked is a separate axis from enabled: a locked account is
                      still enabled, and it is the one people phone about. */}
                  {r.locked && (
                    <span className="ml-1.5 text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                      Locked
                    </span>
                  )}
                  {r.note && (
                    <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">{r.note}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {onUnlock && r.locked && (
                      <RowAction
                        label="Unlock"
                        tone="primary"
                        busy={busy === r.id}
                        onClick={() => onUnlock(r.id)}
                        title="Clear the lockout so they can sign in again"
                      />
                    )}
                    {onLicenses && (
                      <RowAction label="Licenses" busy={busy === r.id} onClick={() => onLicenses(r.id)} />
                    )}
                    {/* Offered even on an AD-synced row, unlike the rest:
                        authentication methods live in the cloud directory, so
                        a hybrid user CAN be helped from here. This is the
                        "lost my phone" call. */}
                    {onResetMfa && (
                      <RowAction
                        label="Reset MFA"
                        busy={busy === r.id}
                        onClick={() => onResetMfa(r.id)}
                        title="Clear their registered sign-in methods so they re-register"
                      />
                    )}
                    {r.disableActions ? (
                      <span
                        className="text-xs text-gray-400 dark:text-gray-500 px-2 py-1"
                        title="This account is synced from Active Directory — change it on the Active Directory tab and the change flows up to Microsoft 365."
                      >
                        Manage in AD
                      </span>
                    ) : (
                      <>
                        {r.enabled ? (
                          <RowAction
                            label="Disable"
                            tone="danger"
                            busy={busy === r.id}
                            onClick={() => onDisable(r.id)}
                          />
                        ) : (
                          <RowAction label="Enable" busy={busy === r.id} onClick={() => onEnable(r.id)} />
                        )}
                        <RowAction label="Reset password" busy={busy === r.id} onClick={() => onReset(r.id)} />
                        <RowAction
                          label="Offboard"
                          tone="danger"
                          busy={busy === r.id}
                          onClick={() => onOffboard(r.id)}
                          title="Disable, sign out everywhere, and (AD) reset + strip groups"
                        />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
